import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo, { localBusinessSchema } from "@/components/Seo";
import { CONTACT } from "@/lib/content";
import { ArrowLeft, Loader2 } from "lucide-react";

const SITE = process.env.REACT_APP_SITE_URL || "https://wtddetailing.ca";

/** Minimal Markdown-to-HTML renderer (safe for our own CRM-generated content).
 *  Supports: # headings, **bold**, *italic*, - lists, [text](url), ![alt](url), paragraphs, blockquote.
 */
function renderMarkdown(md = "") {
  const escape = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const lines = md.split(/\r?\n/);
  const out = [];
  let inList = false, inCode = false;
  const closeList = () => { if (inList) { out.push("</ul>"); inList = false; } };

  const inline = (s) => escape(s)
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="my-6 rounded-xl border border-white/10 max-w-full"/>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-[#1F5EFF] hover:underline">$1</a>')
    .replace(/\*\*([^*]+)\*\*/g, "<strong class='text-white'>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, "<code class='bg-white/10 px-1.5 py-0.5 rounded'>$1</code>");

  for (let raw of lines) {
    const line = raw;
    if (/^```/.test(line)) { inCode = !inCode; out.push(inCode ? "<pre class='bg-black/50 rounded-xl p-4 my-6 overflow-x-auto text-xs'><code>" : "</code></pre>"); continue; }
    if (inCode) { out.push(escape(line) + "\n"); continue; }
    if (/^\s*$/.test(line)) { closeList(); continue; }
    let m;
    if ((m = line.match(/^######\s+(.*)$/))) { closeList(); out.push(`<h6 class="font-display text-base text-white mt-6 mb-2">${inline(m[1])}</h6>`); continue; }
    if ((m = line.match(/^#####\s+(.*)$/))) { closeList(); out.push(`<h5 class="font-display text-lg text-white mt-6 mb-2">${inline(m[1])}</h5>`); continue; }
    if ((m = line.match(/^####\s+(.*)$/))) { closeList(); out.push(`<h4 class="font-display text-xl text-white mt-8 mb-3">${inline(m[1])}</h4>`); continue; }
    if ((m = line.match(/^###\s+(.*)$/))) { closeList(); out.push(`<h3 class="font-display text-2xl text-white mt-10 mb-3">${inline(m[1])}</h3>`); continue; }
    if ((m = line.match(/^##\s+(.*)$/))) { closeList(); out.push(`<h2 class="font-display text-3xl text-white mt-12 mb-4">${inline(m[1])}</h2>`); continue; }
    if ((m = line.match(/^#\s+(.*)$/))) { closeList(); out.push(`<h1 class="font-display text-4xl md:text-5xl text-white mt-8 mb-6">${inline(m[1])}</h1>`); continue; }
    if ((m = line.match(/^>\s+(.*)$/))) { closeList(); out.push(`<blockquote class="border-l-2 border-[#1F5EFF] pl-4 my-6 text-white/70 italic">${inline(m[1])}</blockquote>`); continue; }
    if ((m = line.match(/^[-*]\s+(.*)$/))) { if (!inList) { out.push("<ul class='list-disc pl-6 my-4 space-y-2 text-white/75'>"); inList = true; } out.push(`<li>${inline(m[1])}</li>`); continue; }
    closeList();
    out.push(`<p class='text-white/75 leading-relaxed my-4'>${inline(line)}</p>`);
  }
  closeList();
  return out.join("\n");
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/blog/${slug}`).then(({ data }) => setPost(data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#0c0c0f] text-white flex items-center justify-center">
        <div className="text-center">
          <div className="font-display text-3xl mb-4">Article not found.</div>
          <Link to="/blog" className="text-[#1F5EFF] hover:underline">← Back to blog</Link>
        </div>
      </div>
    );
  }
  if (!post) {
    return (
      <div className="min-h-screen bg-[#0c0c0f] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-white/40 animate-spin"/>
      </div>
    );
  }

  const coverUrl = post.cover_media_id ? `${API}/media/${post.cover_media_id}` : null;

  return (
    <div className="min-h-screen bg-[#0c0c0f] text-white" data-testid={`blog-post-${slug}`}>
      <Seo
        title={post.title}
        description={post.excerpt || post.title}
        path={`/blog/${slug}`}
        image={coverUrl}
        jsonLd={[
          localBusinessSchema({ contact: CONTACT }),
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.excerpt,
            datePublished: post.published_at,
            dateModified: post.updated_at,
            author: { "@type": "Organization", name: "WTD Detailing & Tint", url: SITE },
            publisher: {
              "@type": "Organization",
              name: "WTD Detailing & Tint",
              url: SITE,
              logo: {
                "@type": "ImageObject",
                url: `${SITE}/logo.png`,
              },
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE}/blog/${slug}` },
            image: [coverUrl || `${SITE}/og-image.jpg`],
            inLanguage: "en-CA",
          },
        ]}
      />
      <Navbar />
      <main className="pt-32 pb-24">
        <article className="max-w-3xl mx-auto px-6 md:px-10">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white mb-8">
            <ArrowLeft className="w-4 h-4"/> All articles
          </Link>
          <div className="font-mono-alt text-[10px] text-white/40 mb-4">
            {new Date(post.published_at || post.created_at).toLocaleDateString("en-CA", {
              year: "numeric", month: "long", day: "numeric"
            })}
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-light tracking-tighter leading-tight mb-10">
            {post.title}
          </h1>
          {post.excerpt && (
            <div className="text-white/60 text-lg leading-relaxed mb-12">{post.excerpt}</div>
          )}
          {coverUrl && (
            <img src={coverUrl} alt={post.title} className="w-full aspect-[16/9] object-cover rounded-2xl border border-white/10 mb-12"/>
          )}
          <div className="prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: renderMarkdown(post.content_md) }}/>
        </article>
      </main>
      <Footer />
    </div>
  );
}
